// 移除本地axios与baseURL解析，统一使用通用 apiClient
import apiClient from "@/api/apiClient";

// 登录请求参数接口
export interface LoginRequest {
	address: string;
	inviteCode: string;
	signature: string;
	timestamp: number;
}

// 登录响应数据接口
export interface LoginResponse {
	code: number;
	data: {
		accessToken: string;
		googleAuthEnabled: boolean;
		id: number;
		identity: number;
		image: string;
		inviteCode: string;
		inviteUrl: string;
		level: number;
		locale: string;
		nickName: string;
		originFrom: number;
		parentName: string;
		shareContent: string;
		userName: string;
	};
	msg: string;
}

// 用户余额响应接口
export interface UserBalanceResponse {
	code: number;
	data: {
		fomoBalance: string;
		usdtBalance: string;
		totalAssets: string;
		stakingAmount: string;
		expectedEarnings: string;
		actualEarnings: string;
	};
	msg: string;
}

// 用户信息响应接口
export interface UserInfoResponse {
	code: number;
	data: {
		id: number;
		publicKey: string;
		nickName: string;
		userName: string;
		level: number;
		identity: number;
		image: string;
		inviteCode: string;
		inviteUrl: string;
		locale: string;
		parentName: string;
		shareContent: string;
		googleAuthEnabled: boolean;
		originFrom: number;
		telegramInfo?: string;
		registrationDate: string;
		phone?: string;
		email?: string;
	};
	msg: string;
}

// Mining Dashboard响应接口
export interface MiningDashboardResponse {
	code: number;
	data: {
		fomoTotal: string;
		machineCount: number;
		poolCount: number;
		purchaseCount: number;
	};
	msg: string;
}

// 矿池列表请求参数接口
export interface MiningPurchasesRequest {
	owner: boolean;
	pageNum: number;
	pageSize: number;
	sort: string;
	title: string;
}

// 矿池购买记录接口
export interface MiningPurchase {
	count: number;
	createTime: string;
	id: number;
	machineDuration: number;
	machineId: number;
	machinePrice: string;
	machineTitle: string;
	poolId: number;
	poolTitle: string;
	profit: string;
	profitExpected: string;
	yieldRate: string;
}

export interface MiningPool {
	id: number;
	title: string;
	machineCount: number;
	duration: number;
	profitExpected: string;
	createTime: number;
	status: number;
	yieldRate: string;
	purchaseCode?: string;
}

// 新增：矿池详情响应（用于购买时按ID获取机器列表）
export interface MiningPoolDetailMachine {
	count: number;
	id: number;
	poolId: number;
	price: string;
	remain: number;
	title: string;
}
export interface MiningPoolDetailResponse {
	code: number;
	data: {
		duration: number; // 运行时长（秒）
		id: number;
		limitMin: string; // 最小启动金额
		machineCount?: number; // 矿机数量
		machines: MiningPoolDetailMachine[]; // 矿机列表
		ownerAddress: string; // 创建者地址
		profitExpected: string; // 预期收益 FOMO
		profit: string; // 实际收益 FOMO
		purchase: string; // 挖矿下单量
		purchaseCode?: string; // 邀请码
		stakingFomo: string; // 质押FOMO量
		status?: number; // 状态：0待开启, 1运行中, 3运行完成
		title: string; // 名称
		createTime?: string; // 创建时间（字符串格式）
		owner: boolean; // 是否是拥有者
		reward?: string; // 实际奖励 USDT（仅 owner 为 true 时）
		rewardExpected?: string; // 预期奖励 USDT（仅 owner 为 true 时）
		yieldRate?: string; // 收益率（仅 owner 为 true 时）
		balance?: string; // 池子当前余额（USDT）
		period?: number; // 轮次时间（秒）
		avgPeriodMining?: number; // 每轮总票数 Σsum_t
		soldTotal?: number; // 已售出总量
		currentLevel?: number; // 当前等级
		nextLevel?: number; // 下一等级
		nextPoints?: number; // 下一等级所需积分
	};
	msg: string;
}

// 矿池列表响应接口
export interface MiningPurchasesResponse {
	code: number;
	data: {
		pageNum: number;
		pageSize: number;
		list: MiningPurchase[];
		totalCount: number;
	};
	msg: string;
}

// 矿池列表响应接口
export interface MiningPoolResponse {
	code: number;
	data: {
		pageNum: number;
		pageSize: number;
		list: MiningPool[];
		totalCount: number;
	};
	msg: string;
}

// 矿池会员信息请求参数
export interface MiningPoolMembersRequest {
	nickName: string;
	pageNum: number;
	pageSize: number;
	poolId: number;
}

// 矿池会员信息项
export interface MiningPoolMember {
	gradeId: number;
	gradeIndex: number;
	gradeTitle: string;
	nickName: string;
	points: string;
	reward: string;
	userId: number;
}

// 矿池会员信息响应
export interface MiningPoolMembersResponse {
	code: number;
	data: {
		list: MiningPoolMember[];
		pageNum: number;
		pageSize: number;
		totalCount: number;
	};
	msg: string;
}

// 矿池等级信息项
export interface PoolPointsGrade {
	id: number | null;
	index: number;
	points: string;
	poolId: number;
	ratio: string;
	title: string;
}

// 矿池等级信息响应
export interface PoolPointsInfoResponse {
	code: number;
	data: PoolPointsGrade[];
	msg: string;
}

// 矿机列表请求参数
export interface MiningMachineListRequest {
	owner: boolean;
	pageNum: number;
	pageSize: number;
	sort?: string;
	title?: string;
	poolId?: number; // 用于获取指定矿池的矿机
}

// 矿机列表项
export interface MiningMachineListItem {
	count: number;
	createTime: string;
	duration: number;
	id: number;
	poolId: number;
	price: string;
	profit: string;
	profitExpected: string;
	remain: number;
	status: number;
	title: string;
}

// 矿机列表响应接口
export interface MiningMachineListResponse {
	code: number;
	data: {
		list: MiningMachineListItem[];
		pageNum: number;
		pageSize: number;
		totalCount: number;
	};
	msg: string;
}
// 新增：矿池排行榜响应类型
export interface MiningRankItem {
	id: number;
	profit: string;
	title: string;
	purchaseCode?: string;
}
export interface MiningRankResponse {
	code: number;
	data: MiningRankItem[];
	msg: string;
}
// 用户详情信息接口
export interface UserInfoResponseForLogin {
	code: number;
	data: {
		accessToken: string;
		extEmail: string;
		extPhone: string;
		extTelegram: string;
		extWallet: string;
		googleAuthEnabled: boolean;
		id: number;
		identity: number;
		image: string;
		inviteCode: string;
		inviteUrl: string;
		level: number;
		locale: string;
		nickName: string;
		originFrom: number;
		parentName: string;
		shareContent: string;
		userName: string;
	};
	msg: string;
}

// 资产币种详情接口
export interface AssetDetail {
	symbol: string;
	available: string;
	frozen: string;
	total: string;
	version: number;
	depositNetworks: string[];
	withdrawNetworks: string[];
}

// 充值详情请求参数
export interface DepositViewRequest {
	network: string;
	symbol: string;
}

// 充值详情响应接口
export interface DepositViewResponse {
	code: number;
	data: {
		address: string;
		network: string;
		symbol: string;
		min?: string;
		ca?: string;
		comment?: string;
		qrCode?: string;
	};
	msg: string;
}

// 提币详情请求参数
export interface WithdrawViewRequest {
	network: string;
	symbol: string;
}

// 提币详情响应接口
export interface WithdrawViewResponse {
	code: number;
	data: {
		network: string;
		symbol: string;
		min: string; // 最小提币数量
		fees: string; // 提币手续费
		enable: boolean; // 是否开启提币
		needComment: boolean; // 是否需要填写comment
		closeGoogleAuth: boolean; // 是否关闭谷歌验证
		googleAuthLimit: string; // 谷歌验证需要多少数量的值
		asset: {
			symbol: string;
			available: string;
			frozen: string;
		};
		customer: string; // 客服账号
	};
	msg: string;
}

// 提币请求参数
export interface WithdrawRequest {
	address: string;
	amount: string;
	comment: string;
	from: string; // 固定为 "dapp"
	googleCode: string;
	hash: string; // MD5(initData+symbol+network+address+comment+amount)
	network: string;
	symbol: string;
}

// 提币响应接口
export interface WithdrawResponse {
	code: number;
	data: any; // 根据实际返回结构调整
	msg: string;
}

// 矿机资产信息响应接口
export interface MiningAssetResponse {
	code: number;
	data: {
		assets: Record<string, AssetDetail>;
		totalAssets: string;
		stakingAmount: string;
		expectedEarnings: string;
		actualEarnings: string;
		depositTokens: string[];
		withdrawTokens: string[];
		machineProfitExpected: string; // 矿机预期收益
		machineProfit: string; // 矿机实际收益
		poolRewardExpected: string; // 矿池预期收益
		poolReward: string; // 矿池实际收益
	};
	msg: string;
}

// 购买矿机请求参数接口
export interface BuyMachineItem {
	count: number;
	machineId: number;
	poolId: number;
	purchaseCode?: string;
}

// 购买矿机响应接口
export interface BuyMachineResponse {
	code: number;
	data?: any;
	msg: string;
}

// 预订单机器信息接口
export interface PreOrderMachine {
	count: number;
	createTime: string;
	id: number;
	machineDuration: number;
	machineId: number;
	machinePrice: string;
	machineTitle: string;
	poolId: number;
	poolTitle: string;
	profit: string;
	profitExpected: string;
}

// 创建预订单请求参数接口
export interface CreatePreOrderRequest {
	address: string;
	amount: string;
	hash: string;
	machines: PreOrderMachine[];
	network: string;
	symbol: string;
	type: number;
}

// 创建预订单响应接口
export interface CreatePreOrderResponse {
	code: number;
	data: {
		id: number;
		userId: number;
		network: string;
		symbol: string;
		type: number;
		address: string;
		amount: string;
		err: string | null;
		hash: string;
		status: number;
		text: string;
	};
	msg: string;
}

// 提交订单 hash 请求参数接口
export interface SubmitOrderHashRequest {
	id: number;
	hash: string;
}

// 提交订单 hash 响应接口
export interface SubmitOrderHashResponse {
	code: number;
	data?: any;
	msg: string;
}

// 矿池报告数据点接口
export interface MiningPoolReportDataPoint {
	amount: string;
	time: number;
	timeStr: string;
}

// 矿池报告响应接口
export interface MiningPoolReportResponse {
	code: number;
	data: MiningPoolReportDataPoint[];
	msg: string;
}

// 创建矿池前置数据响应接口
export interface MiningIndexResponse {
	code: number;
	data: {
		poolStakingFomo: string;
		solanaAddress: string;
		purchaseNums: string; // 例如 "50,100,200,500"
		limitNums: string; // 例如 "1000,2000,3000,4000"
	};
	msg: string;
}

// 创建矿池请求参数接口
export interface MiningCreatePoolRequest {
	title: string;
	purchase: string;
	limitMin: string;
	machines: { count: number; price: string; title: string }[];
}

// 创建矿池响应接口
export interface MiningCreatePoolResponse {
	code: number;
	data?: { id?: number } | null;
	msg: string;
}

// 矿池历史订单请求参数接口
export interface MiningHashOrdersRequest {
	hashOrderSymbol?: string; // 币种，FOMOIUSDT 空全部
	hashOrderType?: number | ""; // 链上订单列表(全局) 类型：1:充值 2:支付 3:提币; 空全部
	owner?: boolean;
	pageNum: number; // 当前页码，从1开始
	pageSize: number; // 一页条数，最大50
	poolId?: number; // 矿池ID
	sort?: string; // 排序字段，例如(time asc)多条件 (count desc, time asc); asc 升desc降序
	title?: string; // 名称, 支持模糊查询
}

// 矿池历史订单项接口
export interface MiningHashOrder {
	id: number;
	userId: number;
	network: string;
	symbol: string;
	type: number; // 类型：1:充值 2:支付 3:提币
	address: string;
	amount: string;
	hashUrl: string;
	status: number; // 状态：0:待开启 1:运行中 3:运行完成
	createTime: string;
	poolId?: number;
	poolTitle?: string;
	machineId?: number;
	machineTitle?: string;
	count?: number;
}

// 矿池历史订单响应接口
export interface MiningHashOrdersResponse {
	code: number;
	data: {
		list: MiningHashOrder[];
		pageNum: number;
		pageSize: number;
		totalCount: number;
	};
	msg: string;
}

// 全局矿机购买列表请求参数接口
export interface MachineGlobalPurchasesRequest {
	pageNum: number;
	pageSize: number;
	poolId: number;
	sort?: string;
	title?: string;
}

// 全局矿机购买列表项接口
export interface MachineGlobalPurchase {
	id: number;
	hash: string; // 交易哈希，为空表示余额购买
	createTime: string; // 发起时间
	address: string; // 发起地址
	count: number; // 购买数量
	machineDuration: number; // 剩余时间（秒）
	machineId: number; // 矿机ID
	machinePrice: string; // 价格
	machineTitle: string; // 矿机名称
	poolId: number; // 池子ID
	poolTitle: string; // 矿池名称
	profit: string; // 已获得收益
	profitExpected: string; // 预期收益
}

// 全局矿机购买列表响应接口
export interface MachineGlobalPurchasesResponse {
	code: number;
	data: {
		list: MachineGlobalPurchase[];
		pageNum: number;
		pageSize: number;
		totalCount: number;
	};
	msg: string;
}

// 幸运夺宝列表响应项
export interface TreasureLuckItem {
	amount: string;
	count: number;
	extra: string;
	hits: any[];
	image: string;
	nickName: string;
	poolId: number;
	poolNumberVo: {
		blockDigits: number;
		blockNum: number;
		count: number;
		createTime: string;
		end: number;
		replaces: string;
		start: number;
		step: number;
		type: number;
		usdt: number;
	};
	purchaseType: number;
	type: number;
}

// 幸运夺宝列表响应
export interface TreasureLuckListResponse {
	code: number;
	data: {
		list: TreasureLuckItem[];
		poolId: string;
	};
	msg: string;
}

export const marsAuthApi = {
	// 登录
	login: async (params: LoginRequest): Promise<LoginResponse> => {
		return apiClient.post<LoginResponse>({ url: "/api/web/user2/login", data: params });
	},

	// 获取用户详情信息
	getUserInfo: async (): Promise<UserInfoResponse> => {
		return apiClient.post<UserInfoResponse>({ url: "/api/user/info" });
	},

	// 获取用户余额
	getUserBalance: async (token: string): Promise<UserBalanceResponse> => {
		return apiClient.get<UserBalanceResponse>({
			url: "/api/web/user/balance",
			headers: { Authorization: `Bearer ${token}` },
		});
	},

	// 发送手机验证码（使用 data 传参，type 固定为 6）
	sendPhoneCode: async (phone: string, token: string, type: number = 6, region?: string): Promise<void> => {
		return apiClient.post<void>({
			url: "/api/phone/acquireCode",
			data: { phone, token, type, region },
		});
	},

	// 绑定手机号
	bindPhone: async (phone: string, code: string): Promise<void> => {
		return apiClient.post<void>({
			url: "/api/web/user2/bind-account",
			data: { type: "PHONE", username: phone, verifyCode: code },
		});
	},

	// 发送邮箱验证码（使用 data 传参，type 固定为 6）
	sendEmailCode: async (email: string, token: string = "", type: number = 6): Promise<void> => {
		return apiClient.post<void>({ url: "/api/email/acquireCode", data: { email, token, type } });
	},

	// 绑定邮箱
	bindEmail: async (email: string, code: string): Promise<void> => {
		return apiClient.post<void>({
			url: "/api/web/user2/bind-account",
			data: { type: "EMAIL", username: email, verifyCode: code },
		});
	},

	// 发送 Telegram 验证码
	sendTelegramCode: async (telegramId: number): Promise<void> => {
		return apiClient.post<void>({
			url: "/api/web/user2/telegram/bindcode",
			data: { id: telegramId },
		});
	},

	// 绑定Telegram
	bindTelegram: async (username: string, verifyCode: string): Promise<void> => {
		return apiClient.post<void>({
			url: "/api/web/user2/bind-account",
			data: { type: "TELEGRAM", username, verifyCode },
		});
	},

	// 生成Telegram绑定URL（已废弃，使用 Widget）
	generateTelegramBindUrl: async (): Promise<{ url: string }> => {
		return apiClient.post({ url: "/api/web/user2/telegram/generate-bind-url" });
	},

	// 获取Mining Dashboard数据
	getMiningDashboard: async (): Promise<MiningDashboardResponse> => {
		return apiClient.post<MiningDashboardResponse>({
			url: "/api/web/mining/dashboard",
		});
	},

	// 获取矿池列表数据
	getMiningPool: async (params: MiningPurchasesRequest): Promise<MiningPoolResponse> => {
		return apiClient.post<MiningPoolResponse>({ url: "/api/web/mining/pool/list", data: params });
	},

	// 新增：按ID获取矿池详情（params 传参）
	getMiningPoolDetail: async (id: number): Promise<MiningPoolDetailResponse> => {
		return apiClient.post<MiningPoolDetailResponse>({ url: "/api/web/mining/pool/detail", data: { id } });
	},

	// 获取矿池报告数据（图表数据）
	getMiningPoolReport: async (poolId: number, type: string): Promise<MiningPoolReportResponse> => {
		return apiClient.post<MiningPoolReportResponse>({ url: "/api/web/mining/pool/report", data: { poolId, type } });
	},

	// 获取矿池会员信息
	getMiningPoolMembers: async (params: MiningPoolMembersRequest): Promise<MiningPoolMembersResponse> => {
		return apiClient.post<MiningPoolMembersResponse>({
			url: "/api/web/mining/pool/points/members",
			data: params,
		});
	},

	// 获取矿池等级信息
	getPoolPointsInfo: async (poolId: number): Promise<PoolPointsInfoResponse> => {
		return apiClient.post<PoolPointsInfoResponse>({
			url: "/api/web/mining/pool/points/info",
			data: { poolId },
		});
	},

	// 创建矿池等级
	createPoolPoints: async (data: PoolPointsGrade[]): Promise<{ code: number; msg: string }> => {
		return apiClient.post({
			url: "/api/web/mining/pool/points/create",
			data,
		});
	},

	// 更新矿池等级
	updatePoolPoints: async (data: PoolPointsGrade[]): Promise<{ code: number; msg: string }> => {
		return apiClient.post({
			url: "/api/web/mining/pool/points/update",
			data,
		});
	},

	// 获取矿池列表数据
	getMiningPurchases: async (params: MiningPurchasesRequest): Promise<MiningPurchasesResponse> => {
		return apiClient.post<MiningPurchasesResponse>({
			url: "/api/web/mining/machine/purchases",
			data: params,
		});
	},

	// 获取矿机列表数据
	getMiningMachineList: async (params: MiningMachineListRequest): Promise<MiningMachineListResponse> => {
		return apiClient.post<MiningMachineListResponse>({
			url: "/api/web/mining/machine/list",
			data: params,
		});
	},

	// 获取创建矿池前置数据
	getMiningIndex: async (): Promise<MiningIndexResponse> => {
		return apiClient.post<MiningIndexResponse>({ url: "/api/web/mining/index" });
	},

	// 创建矿池
	createMiningPool: async (params: MiningCreatePoolRequest): Promise<MiningCreatePoolResponse> => {
		return apiClient.post<MiningCreatePoolResponse>({
			url: "/api/web/mining/pool/create",
			data: params,
		});
	},

	// 新增：获取排行榜（type: 1收益[默认]、2热门、3人气）
	getMiningRank: async (type: number = 1): Promise<MiningRankResponse> => {
		return apiClient.post<MiningRankResponse>({
			url: "/api/web/mining/rank",
			data: { type },
		});
	},

	// 获取矿机资产信息
	getMiningAsset: async (): Promise<MiningAssetResponse> => {
		return apiClient.post<MiningAssetResponse>({
			url: "/api/web/mining/asset",
		});
	},

	// 获取充值详情
	getDepositView: async (params: DepositViewRequest): Promise<DepositViewResponse> => {
		return apiClient.post<DepositViewResponse>({
			url: "/api/wallet/deposit-view",
			data: params,
		});
	},

	// 获取提币详情
	getWithdrawView: async (params: WithdrawViewRequest): Promise<WithdrawViewResponse> => {
		return apiClient.post<WithdrawViewResponse>({
			url: "/api/wallet/withdraw-view",
			data: params,
		});
	},

	// 提交提币请求
	submitWithdraw: async (params: WithdrawRequest): Promise<WithdrawResponse> => {
		return apiClient.post<WithdrawResponse>({
			url: "/api/wallet/withdraw",
			data: params,
		});
	},

	// 购买矿机
	buyMachine: async (items: BuyMachineItem[]): Promise<BuyMachineResponse> => {
		return apiClient.post<BuyMachineResponse>({
			url: "/api/web/mining/machine/buy",
			data: items,
		});
	},

	// 创建预订单
	createPreOrder: async (params: CreatePreOrderRequest): Promise<CreatePreOrderResponse> => {
		return apiClient.post<CreatePreOrderResponse>({
			url: "/api/web/mining/machine/hash/pre-order",
			data: params,
		});
	},

	// 提交订单 hash
	submitOrderHash: async (params: SubmitOrderHashRequest): Promise<SubmitOrderHashResponse> => {
		return apiClient.post<SubmitOrderHashResponse>({
			url: "/api/web/mining/machine/hash/order",
			data: params,
		});
	},

	// 获取矿池历史订单列表
	getMiningHashOrders: async (params: MiningHashOrdersRequest): Promise<MiningHashOrdersResponse> => {
		return apiClient.post<MiningHashOrdersResponse>({
			url: "/api/web/mining/hash/orders",
			data: params,
		});
	},

	// 获取全局矿机购买列表
	getMachineGlobalPurchases: async (params: MachineGlobalPurchasesRequest): Promise<MachineGlobalPurchasesResponse> => {
		return apiClient.post<MachineGlobalPurchasesResponse>({
			url: "/api/web/mining/machine/global/purchases",
			data: params,
		});
	},

	// 退出登录
	loginOut: async (): Promise<{ code: number; msg: string; data: any }> => {
		return apiClient.post({
			url: "/api/web/home/loginOut",
			data: {},
		});
	},

	// 获取幸运夺宝中奖列表
	getTreasureLuckList: async (): Promise<TreasureLuckListResponse> => {
		return apiClient.post<TreasureLuckListResponse>({
			url: "/api/web/staking/luck-list",
		});
	},
};

export default marsAuthApi;
